/**
 * File Upload Status Types
 *
 * Defines the upload lifecycle states and transitions.
 * Following a state machine pattern to prevent invalid states.
 *
 * State Machine:
 * PENDING → UPLOADING → COMPLETED (simple files)
 *         ↓          ↘
 *         ↓           PROCESSING → COMPLETED (files needing text extraction)
 *       CANCELLED
 */

/**
 * Upload status for a file attachment
 *
 * - PENDING: File selected, waiting to start upload
 * - UPLOADING: File currently uploading to server
 * - PROCESSING: File uploaded, extracting text content (XLSX, PDF, etc.)
 * - COMPLETED: Upload and processing successful, ready to send
 * - FAILED: Upload or processing failed, can be retried
 * - CANCELLED: Upload cancelled by user
 */
export type UploadStatus =
  | "PENDING"
  | "UPLOADING"
  | "PROCESSING"
  | "COMPLETED"
  | "FAILED"
  | "CANCELLED";

/**
 * Valid status transitions
 * Used to enforce state machine rules and prevent invalid state changes
 */
export const VALID_STATUS_TRANSITIONS: Record<UploadStatus, UploadStatus[]> = {
  PENDING: ["UPLOADING", "CANCELLED"],
  UPLOADING: ["PROCESSING", "COMPLETED", "FAILED", "CANCELLED"],
  PROCESSING: ["COMPLETED", "FAILED"],
  COMPLETED: [], // Terminal state
  FAILED: ["UPLOADING", "CANCELLED"], // Can retry
  CANCELLED: [] // Terminal state
};

/**
 * Check if a status transition is valid
 *
 * @param from - Current status
 * @param to - Target status
 * @returns True if transition is allowed
 *
 * @example
 * isValidTransition('PENDING', 'UPLOADING') // true
 * isValidTransition('COMPLETED', 'UPLOADING') // false
 */
export function isValidStatusTransition(from: UploadStatus, to: UploadStatus): boolean {
  return VALID_STATUS_TRANSITIONS[from].includes(to);
}

/**
 * Upload progress information
 * Used for real-time progress tracking via WebSocket
 */
export interface UploadProgress {
  /** Bytes uploaded so far */
  loaded: number;
  /** Total bytes to upload */
  total: number;
  /** Progress percentage (0-100) */
  percentage: number;
}
